import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  Checkbox,
  ListItemText,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { getPaymentStaffInfo } from '../services/payments';

const useStyles = makeStyles(theme => ({
  table: {
    marginTop: 10,
    padding: 5,
    '& .MuiTableCell-sizeSmall': {
      padding: theme.spacing(0.25),
    }
  },
  header: {
    background: theme.palette.primary.main,
  },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
    userSelect: 'none',
    '& .MuiTableSortLabel-root': { color: theme.palette.common.white },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': { color: theme.palette.common.white },
    '& .MuiTableSortLabel-icon': { color: theme.palette.common.white + ' !important' },
  },
  noResultsBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    textAlign: 'center',
  },
  filtersBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  formControl: { minWidth: 160 },
  grow: { flex: 1 },
}));

const BoolSelect = ({ label, name, value, onChange, className }) => (
  <FormControl variant="outlined" size="small" className={className}>
    <InputLabel id={`${name}-label`}>{label}</InputLabel>
    <Select
      labelId={`${name}-label`}
      value={value}
      onChange={onChange}
      label={label}
      name={name}
    >
      <MenuItem value="">Todos</MenuItem>
      <MenuItem value="true">Sí</MenuItem>
      <MenuItem value="false">No</MenuItem>
    </Select>
  </FormControl>
);

export default function ComercialTable() {
  const rowColors = {
    rojo: '#ffcccc',
    verde: '#ccffcc',
    amarillo: '#fff9cc',
    gris: '#e0e0e0',
  };

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const pageSizeOptions = [10, 25, 50, 100];

  const initialFilters = {
    clientes_pagos: '',
    plan: [],
    email: '',
    nombre: '',
    compliant: '',
    vencido: '',
    compra_pendiente_asignacion: '',
    downgrade_agendado: '',
    renovacion_agendada: '',
    tiene_perks: '',
    creditos_asignados: '',
    estado_compra: '',
    semaforo: '',
    dias_restantes__gte: '',
    dias_restantes__lte: '',
    tiene_costos_fijos: '',
  };
  const [filters, setFilters] = useState(initialFilters);
  const [applyToken, setApplyToken] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const planOptions = ['Todos', 'Gratuito', 'Basico', 'Lite', 'Profesional', 'Experto', 'Staff'];

  const columns = [
    { id: 'email', label: 'Email', type: 'string' },
    { id: 'plan_actual', label: 'Plan actual', type: 'string' },
    { id: 'total', label: 'Total', type: 'number' },
    { id: 'estado_compra', label: 'Estado compra', type: 'string' },
    { id: 'dias_restantes', label: 'Días restantes', type: 'number' },
    { id: 'fecha_ultima_compra', label: 'Fecha última compra', type: 'string' },
    { id: 'valor_mensual_total', label: 'Valor mensual total', type: 'number' },
    { id: 'historico_total_abonado  ', label: 'Histórico total abonado', type: 'number' }
  ];

  const orderByOptions = [
    { value: 'plan_actual', label: 'Plan (A-Z)' },
    { value: '-plan_actual', label: 'Plan (Z-A)' },
    { value: 'nombre', label: 'Nombre (A-Z)' },
    { value: '-nombre', label: 'Nombre (Z-A)' },
    { value: 'email', label: 'Email (A-Z)' },
    { value: '-email', label: 'Email (Z-A)' },
    { value: 'fecha_ultima_compra', label: 'Fecha última compra' },
    { value: 'proximo_vencimiento', label: 'Próximo vencimiento' },
    { value: 'valor_mensual_total', label: 'Valor mensual total' }
  ];


  const [orderBy, setOrderBy] = useState('email');

  const sortedInfo = info;

  const totalPages = useMemo(() => {
    if (!total || !pageSize) return 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const pageOptions = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages]
  );

  useEffect(() => {
    getPaymentsInfo(page, pageSize);
  }, [page, pageSize, applyToken]);

  const getPaymentsInfo = async (pageNumber = 1, perPage = 10) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) params[k] = v;
      });
      params['order_by'] = orderBy;
      const response = await getPaymentStaffInfo(pageNumber, perPage, params);
      const data = (response && response.data && response.data.results) || [];
      const totalFromApi = (response && (response.count ?? (response.data && response.data.count))) ?? 0;
      setInfo(Array.isArray(data) ? data : []);
      setTotal(Number(totalFromApi) || 0);
    } catch {
      setInfo([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = event => setPage(Number(event.target.value));
  const handleChangePageSize = event => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };
  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

  const onChangeFilter = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  const applyFilters = () => {
    setPage(1);
    setApplyToken(t => t + 1);
  };
  const clearFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    setApplyToken(t => t + 1);
  };

  const handleCellClick = item => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString();
  }

  const ALL_OPTION = 'Todos';
  const realOptions = planOptions.filter(p => p !== ALL_OPTION);

  return (
    <>
      {loading ? (
        <Box mt={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box p={4} pb={0} display="flex" alignItems="center" gap={8}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowFilters(s => !s)}
            >
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </Button>
          </Box>

          <Collapse in={showFilters}>
            <Box p={4} pb={0}>
              <Box className={classes.filtersBar}>
                <FormControl variant="outlined" size="small">
                  <InputLabel id="plan-label">Plan</InputLabel>
                  <Select
                    multiple
                    value={filters.plan}
                    onChange={(e) => {
                      const value = e.target.value;

                      const clickedAll = value.includes(ALL_OPTION);
                      const allSelected = filters.plan.length === realOptions.length;

                      if (clickedAll) {
                        setFilters(prev => ({
                          ...prev,
                          plan: allSelected ? [] : realOptions
                        }));
                        return;
                      }

                      setFilters(prev => ({
                        ...prev,
                        plan: value
                      }));
                    }}
                    renderValue={(selected) => selected.join(', ')}
                    variant="outlined"
                    size="small"
                  >
                    {planOptions.map(plan => {
                      const isAll = plan === ALL_OPTION;
                      const allSelected = filters.plan.length === realOptions.length;

                      return (
                        <MenuItem key={plan} value={plan}>
                          <Checkbox
                            color="primary"
                            checked={
                              isAll
                                ? allSelected
                                : filters.plan.includes(plan)
                            }
                          />
                          <ListItemText primary={plan} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField label="Email" name="email" value={filters.email} onChange={onChangeFilter} variant="outlined" size="small" />
                <TextField label="Nombre" name="nombre" value={filters.nombre} onChange={onChangeFilter} variant="outlined" size="small" />
                <FormControl variant="outlined" size="small">
                  <InputLabel id="plan-label">Estado compra</InputLabel>
                  <Select
                    labelId="plan-label"
                    name="estado_compra"
                    value={filters.estado_compra}
                    onChange={onChangeFilter}
                    label="Estado compra"
                  >
                    <MenuItem value="Pagado">Pagado</MenuItem>
                    <MenuItem value="Pendiente de Pago">Pendiente de Pago</MenuItem>
                    <MenuItem value="Fallido">Fallido</MenuItem>
                    <MenuItem value="Finalizada">Finalizada</MenuItem>
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small">
                  <InputLabel id="plan-label">Semáforo</InputLabel>
                  <Select
                    labelId="plan-label"
                    name="semaforo"
                    value={filters.semaforo}
                    onChange={onChangeFilter}
                    label="Semáforo"
                  >
                    <MenuItem value="rojo">Rojo</MenuItem>
                    <MenuItem value="amarillo">Amarillo</MenuItem>
                    <MenuItem value="verde">Verde</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Días restantes ≥" name="dias_restantes__gte" type="number" value={filters.dias_restantes__gte} onChange={onChangeFilter} variant="outlined" size="small" />
                <TextField label="Días restantes ≤" name="dias_restantes__lte" type="number" value={filters.dias_restantes__lte} onChange={onChangeFilter} variant="outlined" size="small" />
                <BoolSelect label="Clientes pagos" name="clientes_pagos" value={filters.clientes_pagos} onChange={onChangeFilter} />
                <BoolSelect label="Compliant" name="compliant" value={filters.compliant} onChange={onChangeFilter} />
                <BoolSelect label="Vencido" name="vencido" value={filters.vencido} onChange={onChangeFilter} />
                <BoolSelect label="Compra pend. asignación" name="compra_pendiente_asignacion" value={filters.compra_pendiente_asignacion} onChange={onChangeFilter} />
                <BoolSelect label="Downgrade agendado" name="downgrade_agendado" value={filters.downgrade_agendado} onChange={onChangeFilter} />
                <BoolSelect label="Renovación agendada" name="renovacion_agendada" value={filters.renovacion_agendada} onChange={onChangeFilter} />
                <BoolSelect label="Tiene perks" name="tiene_perks" value={filters.tiene_perks} onChange={onChangeFilter} />
                <BoolSelect label="Créditos asignados" name="creditos_asignados" value={filters.creditos_asignados} onChange={onChangeFilter} />
                <BoolSelect label="Tiene costos fijos" name="tiene_costos_fijos" value={filters.tiene_costos_fijos} onChange={onChangeFilter} />
                <Select
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                  label="Ordenar por"
                >
                  {orderByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box display="flex" gap={8} mb={2}>
                <Button variant="contained" color="primary" onClick={applyFilters}>Aplicar filtros</Button>
                <Button onClick={clearFilters}>Limpiar</Button>
              </Box>
            </Box>
          </Collapse>

          <Box p={4} pt={0}>
            {sortedInfo && sortedInfo.length > 0 ? (
              <>
                <TableContainer component={Paper}>
                  <Table size="small" aria-label="Historial" className={classes.table}>
                    <TableHead>
                      <TableRow className={classes.header}>
                        {columns.map(col => (
                          <TableCell
                            key={col.id}
                            align="center"
                            className={classes.headerCell}
                          >

                            {col.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedInfo.map(item => (
                        <TableRow
                          key={item.id}
                          hover
                          style={{
                            backgroundColor: rowColors[(item?.semaforo || '').toLowerCase()] || 'transparent'
                          }}
                        >
                          <TableCell align="center">{item?.email || '-'}</TableCell>
                          <TableCell align="center">{item?.plan_actual || '-'}</TableCell>
                          <TableCell align="center">{item?.total ?? '-'}</TableCell>
                          <TableCell align="center">{item?.estado_compra || '-'}</TableCell>
                          <TableCell align="center">{(item?.dias_restantes === 0 && item?.dias_adeudados > 0) ? `-${item?.dias_adeudados}` : item?.dias_restantes}</TableCell>
                          <TableCell align="center">{formatDate(item?.fecha_ultima_compra) || '-'}</TableCell>
                          <TableCell align="center">{`U$S ${item?.valor_mensual_total.toFixed(2)}`}</TableCell>
                          <TableCell align="center">{`U$S ${item?.historico_total_abonado.toFixed(2)}`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box className={classes.toolbar}>
                  <FormControl variant="outlined" size="small" className={classes.formControl}>
                    <InputLabel id="page-size-label">Cantidad</InputLabel>
                    <Select labelId="page-size-label" value={pageSize} onChange={handleChangePageSize} label="Cantidad por página">
                      {pageSizeOptions.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" size="small" className={classes.formControl} disabled={totalPages === 0}>
                    <InputLabel id="page-label">Página</InputLabel>
                    <Select labelId="page-label" value={Math.min(page, totalPages || 1)} onChange={handleChangePage} label="Página">
                      {pageOptions.map(p => (
                        <MenuItem key={p} value={p}>{p}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <IconButton onClick={handlePrev} disabled={page <= 1} aria-label="Anterior">
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton onClick={handleNext} disabled={page >= totalPages} aria-label="Siguiente">
                    <ChevronRightIcon />
                  </IconButton>

                  <Box className={classes.grow} />
                  <Box>Página {page} de {totalPages || 1} — Total: {total}</Box>
                </Box>
              </>
            ) : (
              <Box className={classes.noResultsBox}>No hay resultados.</Box>
            )}
          </Box>
        </>
      )}

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle</DialogTitle>
        <DialogContent dividers>
          {selectedItem && (
            <Box component="dl" display="grid" gridTemplateColumns="1fr 1fr" gap={4}>
              {Object.entries(selectedItem).map(([key, value]) => (
                <React.Fragment key={key}>
                  <Box component="dt" fontWeight="bold" color="primary.main">
                    {key}
                  </Box>
                  <Box component="dd" m={0} style={{ wordBreak: 'break-word' }}>
                    {typeof value === 'object'
                      ? JSON.stringify(value, null, 2)
                      : String(value)}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
